/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Prop, State, h, Method, Element, Build } from '@stencil/core';
import { ButtonOptions, ButtonType } from '../../static/buttons/button.interface';
import { JSXAttributes } from '@stencil/core/internal';
import { kebabCase } from 'case-anything';
import { addStringIfUnique, serialize } from '../../../utils';

export type MenuType = 'mobileMenu' | 'accountMenu';

@Component({
    tag: 'web-mobile-menu',
    styleUrl: './mobile-menu.scss',
    shadow: true
})
export class WebMobileMenu {
    @Element() hostElement: HTMLWebMobileMenuElement;

    @Prop({ mutable: true }) loggedIn = false;
    @Prop({ mutable: true }) userName = undefined;
    @Prop({ mutable: true }) menuTitle: string = 'Menu';
    @Prop({ mutable: true }) menuType: MenuType = 'mobileMenu';
    @Prop({ mutable: true, reflect: true }) ariaHidden = 'true';

    @Prop() backButtonAriaLabel: string = 'Back';
    @Prop() closeButtonAriaLabel: string = 'Close';

    @Prop() apiEndpoint: string = '/static/api';

    @State() data: any = '';
    @State() menus: string[] = [];

    private bodyOverflow: string;
    private rootMenuName: string;
    private dialog?: HTMLDialogElement;
    private backButton?: HTMLButtonElement;

    async componentWillLoad() {
        if (Build.isServer) {
            if (this.menuType === 'mobileMenu') {
                try {
                    this.data = JSON.parse(this.hostElement.querySelector('script').innerText);
                } catch (x) {
                    this.data = [];
                }
            } else this.data = [];
            this.hostElement.innerHTML = '';
            return;
        }
        const response = await fetch(`${this.apiEndpoint}/${kebabCase(this.menuType)}.json`);
        const data = await response.json();
        this.data = structuredClone(data);
        this.rootMenuName = this.data.find(({ isRoot }) => isRoot).name;
    }

    componentDidLoad() {
        if (Build.isServer) return;
        this.checkForCurrentLink();
    }

    showMenu = (name: string, animate = true) => {
        const menu = this.data.find((item) => item.name === name);
        this.menuTitle = menu.title;
        this.menus = addStringIfUnique(this.menus)(menu.name);
        this.backButton.style.visibility = menu.isRoot ? 'hidden' : 'visible';

        const menuLevelElements = this.hostElement.shadowRoot.querySelectorAll('[data-menu-name]');
        menuLevelElements.forEach((element) => {
            const menuName = (element as HTMLElement).dataset['menuName'];
            const visible = this.menus.includes(menuName);
            const interactive = this.menus.indexOf(menuName) === this.menus.length - 1;
            this.toggleSubmenu(element, visible, animate);
            this.toggleInert(element, interactive);
            element.setAttribute('aria-hidden', interactive ? 'false' : 'true');
            const controllingElements = this.hostElement.shadowRoot.querySelectorAll(`[data-menu-link=${menuName}]`);
            controllingElements.forEach((menuLink) =>
                menuLink.setAttribute('aria-expanded', visible ? 'true' : 'false')
            );
        });
    };

    private toggleSubmenu(element: Element, toggle: boolean, animate: boolean) {
        const classes = element.classList;
        classes.toggle('active', toggle);
        classes.toggle('inactive', !toggle);
        classes.toggle('animate', animate);
    }

    private toggleInert(element, toggle) {
        if (toggle) {
            element.removeAttribute('inert');
        } else {
            element.setAttribute('inert', '');
        }
    }

    /**
     * Opens the mobile menu.
     */
    @Method()
    async open(data) {
        this.openMenu(data);
        this.ariaHidden = 'false';
    }

    /**
     * Closes the mobile menu.
     */
    @Method()
    async close() {
        this.ariaHidden = 'true';
        this.closeMenu();
    }

    private openMenu({ status, userName }) {
        this.dialog.showModal();

        if (this.menuType === 'accountMenu') {
            this.loggedIn = status;
            this.userName = userName;
        }

        this.showMenu(this.rootMenuName, false);
        this.bodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.body.setAttribute('inert', '');
        this.setAriaExpanded('true');
    }

    private closeMenu() {
        this.menus = [];
        document.body.style.overflow = this.bodyOverflow;
        document.body.removeAttribute('inert');
        this.setAriaExpanded('false');
        const menuElements = this.hostElement.shadowRoot.querySelectorAll('[data-menu-name]');
        menuElements.forEach((element) => {
            element.setAttribute('aria-hidden', 'true');
        });
        this.dialog.close();
    }

    private setAriaExpanded(expanded: string) {
        const { id } = this.hostElement;
        const controllers = id ? Array.from(document.querySelectorAll(`[aria-controls=${id}]`)) : [];
        controllers.forEach((node) => node.setAttribute('aria-expanded', expanded));
    }

    private goBack() {
        this.menus.pop();
        const previousMenu = this.menus[this.menus.length - 1];
        this.showMenu(previousMenu);
    }

    private handleListClick = (event: Event): void => {
        const link: HTMLAnchorElement = (event.target as HTMLElement).closest('a[href]');
        if (!link) {
            return;
        }

        // An external listener may call preventDefault() on this event to handle this click instead.
        // The underlying native click must be prevented as to not follow any hrefs automatically.
        if (!this.dispatchEvent('webMobileMenuNavigate', { mobileMenu: this, link })) {
            event.preventDefault();
            return;
        }

        const menuName = link.dataset['menuLink'];
        if (menuName) {
            event.preventDefault();
            this.showMenu(menuName);
            link.setAttribute('aria-expanded', 'true');
        }
    };

    private handleButtonClick = (url: string): void => {
        const link = document.createElement('a');
        link.setAttribute('href', url);
        if (this.dispatchEvent('webMobileMenuNavigate', { mobileMenu: this, link })) {
            window.location.href = url;
        }
    };

    private dispatchEvent(type: string, detail: Record<string, any>): boolean {
        const customEvent = new CustomEvent(type, { detail, bubbles: true, cancelable: true });
        return this.hostElement.dispatchEvent(customEvent);
    }

    @Method()
    async checkForCurrentLink() {
        const [path] = window.location.pathname.match(/\/[^/]*/);
        const links = this.hostElement.shadowRoot.querySelectorAll('a');
        Array.from(links).forEach((link) => {
            const href = link.getAttribute('href');
            const linkPath = href?.match(/\/[^#]*/);
            if (linkPath && linkPath[0] === path) {
                link.setAttribute('aria-current', 'page');
            } else {
                link.removeAttribute('aria-current');
            }
        });
    }

    private renderComponent = (component): JSXAttributes<Element> => {
        const { type, ...props } = component;
        switch (type) {
            case 'list':
                return <web-list onClick={this.handleListClick} items={serialize(props.items)} />;
            case 'menuGrid':
                return this.renderGrid(props);
            case 'link':
                return (
                    <web-link
                        {...props}
                        linkText={props.text}
                        linkTitle={props.text}
                        iconStart={props.iconStart?.name}
                        iconEnd={props.iconEnd?.name}
                    />
                );
            case 'button':
                return (
                    <web-button
                        {...(props as ButtonOptions)}
                        buttonText={props.text}
                        linkTitle={props.text}
                        type={ButtonType.secondary}
                        iconStart={props.iconStart?.name}
                        iconEnd={props.iconEnd?.name}
                        fullWidth={true}
                        onClick={() => this.handleButtonClick(props.url)}
                    />
                );
            case 'divider':
                return <hr />;
        }
    };

    private renderGrid = (item): JSXAttributes<Element> => (
        <div class="grid-item" role="menuitem">
            <div class="h4 item-heading">{item.title}</div>
            <p>{item.content}</p>
            {item.gridItems.map(this.renderComponent)}
        </div>
    );

    private renderMenu = (menuItem: {
        name: string;
        title: string;
        items: unknown[];
        isRoot: boolean;
    }): JSXAttributes<Element> => (
        <div
            id={menuItem.name}
            data-menu-name={menuItem.name}
            title={menuItem.title}
            role="navigation"
            {...(!menuItem.isRoot && { 'aria-labelledby': menuItem.name + '-trigger' })}
            inert
        >
            {this.userName && (
                <div class="user-name">
                    <web-icon name="logged-in-state"></web-icon>
                    {this.userName}
                </div>
            )}
            {menuItem.items.map(this.renderComponent)}
        </div>
    );

    render = (): JSXAttributes<Element> =>
        this.data && (
            <nav>
                <dialog ref={(el) => (this.dialog = el as HTMLDialogElement)}>
                    <header part="header">
                        <button
                            aria-label={this.backButtonAriaLabel}
                            ref={(el) => (this.backButton = el as HTMLButtonElement)}
                            onClick={(ev) => {
                                ev.preventDefault();
                                this.goBack();
                            }}
                        >
                            <span>
                                <web-link icon-start="chevron-left" type="secondary" url="#"></web-link>
                            </span>
                        </button>
                        <div part="menu-title">{this.menuTitle}</div>
                        <button
                            aria-label={this.closeButtonAriaLabel}
                            onClick={(ev) => {
                                ev.preventDefault();
                                this.close();
                            }}
                        >
                            <span class="close-button">
                                <web-link icon-end="close" type="primary" url="#"></web-link>
                            </span>
                        </button>
                    </header>
                    <hr part="divider" />
                    <main>{this.data.map(this.renderMenu)}</main>
                </dialog>
            </nav>
        );
}
